import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import logger from '../../utils/logger';
import styles from './CourseLibrary.module.css';
import YearLevelManagement from './YearLevelManagement';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
// Empty = use main server /api/upload-image
const IMAGE_SERVICE_URL = process.env.REACT_APP_IMAGE_UPLOAD_URL || '';

function getAdminPanelAuthHeaders(includeJsonContentType = false) {
  const token =
    sessionStorage.getItem('token') ||
    sessionStorage.getItem('adminToken') ||
    localStorage.getItem('token') ||
    localStorage.getItem('adminToken');
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  if (includeJsonContentType) headers['Content-Type'] = 'application/json';
  return headers;
}

const CourseLibrary = ({ onBack }) => {
  const { t } = useTranslation('courseLibrary');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [teachers, setTeachers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [students, setStudents] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [settings, setSettings] = useState({});
  const [supportClasses, setSupportClasses] = useState([]);
  const [editingSupportClass, setEditingSupportClass] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [backendStatus, setBackendStatus] = useState('checking');
  const [newSupportClass, setNewSupportClass] = useState({
    title: '',
    description: '',
    type: 'recorded',
    school_level: 'primary',
    subject: '',
    year: '',
    teacher_id: 1,
    cover_image_url: '',
    price: 0,
    enrollment_target: 0
  });
  const [selectedCourseType, setSelectedCourseType] = useState(null);
  const [courseTypeImages, setCourseTypeImages] = useState({
    recorded: '/assets/images/recorded_courses.jpg',
    workshops: '/assets/images/workshops.jpg',
    normal: '/assets/images/normal_classes.jpg'
  });
  const [editingCourseTypeImage, setEditingCourseTypeImage] = useState(null);
  const [showImageEditModal, setShowImageEditModal] = useState(false);
  const [selectedSchoolLevel, setSelectedSchoolLevel] = useState(null);
  const [selectedYearLevel, setSelectedYearLevel] = useState(null);
  const [activeYearTab, setActiveYearTab] = useState(null);
  const [yearLevels, setYearLevels] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [educationalContent, setEducationalContent] = useState([]);
  const [showCreateContentModal, setShowCreateContentModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [newContent, setNewContent] = useState({
    title: '',
    content: '',
    content_type: 'lesson',
    difficulty_level: 'beginner',
    subject_id: '',
    is_published: false
  });
  const [newCourse, setNewCourse] = useState({
    title: '',
    category: '',
    price: '',
    original_price: '',
    level: 'beginner',
    description: '',
    teacher_id: '',
    duration_minutes: 0,
    thumbnail_url: '',
    cover_image_url: '',
    status: 'active'
  });
  const [showCreateCourseModal, setShowCreateCourseModal] = useState(false);
  const [isCreatingCourse, setIsCreatingCourse] = useState(false);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [courseVideos, setCourseVideos] = useState([]);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [showEditCourseModal, setShowEditCourseModal] = useState(false);
  const [viewingCourse, setViewingCourse] = useState(null);
  const [showViewCourseModal, setShowViewCourseModal] = useState(false);

  const tabs = [
    { id: 'dashboard', label: t('tabs.dashboard'), icon: 'dashboard' },
    { id: 'teachers', label: t('tabs.teachers'), icon: 'school' },
    { id: 'courses', label: t('tabs.courses'), icon: 'video_library' },
    { id: 'add-course', label: 'Add Course', icon: 'add_circle' },
    { id: 'students', label: t('tabs.students'), icon: 'people' },
    { id: 'support-classes', label: t('tabs.supportClasses'), icon: 'support_agent' },
    { id: 'payments', label: t('tabs.payments'), icon: 'payments' },
    { id: 'reviews', label: t('tabs.reviews'), icon: 'star_rate' },
    { id: 'analytics', label: t('tabs.analytics'), icon: 'analytics' },
    { id: 'settings', label: t('tabs.settings'), icon: 'settings' }
  ];

  useEffect(() => {
    loadSampleData();
    checkBackendHealth();
    loadTeachers();
    loadYearLevels();
    loadCourseTypeImages();
    loadCourses();
  }, []);

  // Load courses from API
  const loadCourses = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin-course-management/courses`, {
        headers: getAdminPanelAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setCourses(data.courses || []);
      } else {
        logger.error('Failed to load courses');
        // Keep sample data as fallback
      }
    } catch (error) {
      logger.error('Error loading courses:', error);
      // Keep sample data as fallback
    }
  };

  // Load teachers from API
  const loadTeachersFromAPI = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin-course-management/teachers`, {
        headers: getAdminPanelAuthHeaders(),
      });
      if (response.ok) {
        const teachers = await response.json();
        // Merge with existing teachers
        setTeachers(prev => {
          const apiTeachers = teachers.map(t => ({
            id: t.id,
            name: t.name,
            email: t.email,
            avatar: t.avatar
          }));
          return [...apiTeachers, ...prev.filter(t => !apiTeachers.find(at => at.id === t.id))];
        });
      }
    } catch (error) {
      logger.error('Error loading teachers:', error);
    }
  };

  const loadCourseTypeImages = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/course-type-images/course-type-images`);
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.images) {
          setCourseTypeImages(prev => ({
            ...prev,
            ...data.images
          }));
        }
      }
    } catch (error) {
      logger.error('Error loading course type images:', error);
    }
  };

  useEffect(() => {
    if (selectedSchoolLevel) {
      loadYearLevelsBySchoolLevel(selectedSchoolLevel);
    }
  }, [selectedSchoolLevel]);

  useEffect(() => {
    if (selectedYearLevel) {
      loadYearLevelDetails(selectedYearLevel);
    }
  }, [selectedYearLevel]);

  useEffect(() => {
    if (activeYearTab) {
      const yearLevel = yearLevels.find(yl => yl.id === activeYearTab);
      if (yearLevel) {
        loadYearLevelDetails(yearLevel);
      }
    }
  }, [activeYearTab, yearLevels]);

  const checkBackendHealth = async () => {
    try {
      const response = await fetch(`${IMAGE_SERVICE_URL}/health`);
      if (response.ok) {
        logger.log('✅ Backend image upload service is running');
        setBackendStatus('online');
      } else {
        logger.warn('⚠️ Backend image upload service may not be running');
        setBackendStatus('offline');
      }
    } catch (error) {
      logger.warn('⚠️ Cannot connect to backend image upload service:', error.message);
      setBackendStatus('offline');
    }
  };

  const handleEditSupportClass = (supportClass) => {
    setEditingSupportClass(supportClass);
    setShowEditModal(true);
  };

  const handleDeleteSupportClass = (supportClassId) => {
    if (window.confirm(t('messages.deleteConfirm'))) {
      setSupportClasses(prev => prev.filter(sc => sc.id !== supportClassId));
    }
  };

  const handleSaveSupportClass = (updatedSupportClass) => {
    setSupportClasses(prev => prev.map(sc => 
      sc.id === updatedSupportClass.id ? updatedSupportClass : sc
    ));
    setShowEditModal(false);
    setEditingSupportClass(null);
  };

  const loadTeachers = async () => {
    try {
      // Try to load from admin API first
      const response = await fetch(`${API_BASE_URL}/api/admin-course-management/teachers`, {
        headers: getAdminPanelAuthHeaders(),
      });
      if (response.ok) {
        const teachers = await response.json();
        setTeachers(teachers.map(t => ({
          id: t.id,
          name: t.name,
          email: t.email,
          avatar: t.avatar
        })));
        return;
      }
    } catch (error) {
      logger.error('Error loading teachers from API:', error);
    }
    
    // Fallback to sample data
    setTeachers([
      { id: 1, name: 'Dr. Sarah Johnson', email: 'sarah@example.com' },
      { id: 2, name: 'Prof. Michael Chen', email: 'michael@example.com' },
      { id: 3, name: 'Emma Wilson', email: 'emma@example.com' },
      { id: 4, name: 'Lisa Thompson', email: 'lisa@example.com' }
    ]);
  };

  const handleCreateSupportClass = () => {
    // Pre-fill the course type if one is selected
    if (selectedCourseType) {
      setNewSupportClass(prev => ({
        ...prev,
        type: selectedCourseType
      }));
    }
    setShowCreateModal(true);
  };

  const handleCancelCreate = () => {
    setShowCreateModal(false);
    setNewSupportClass({
      title: '',
      description: '',
      type: 'recorded',
      school_level: 'primary',
      subject: '',
      year: '',
      teacher_id: 1,
      cover_image_url: '',
      price: 0,
      enrollment_target: 0
    });
  };

  const handleSaveNewSupportClass = async (newSupportClass) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/support-classes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newSupportClass),
      });

      if (response.ok) {
        const result = await response.json();
        // Add the new support class to the list
        const newClass = {
          id: result.data.id,
          ...newSupportClass,
          enrollments: 0,
          revenue: 0,
          rating: 0,
          thumbnail: newSupportClass.cover_image_url || '/assets/images/default-course.jpg'
        };
        setSupportClasses(prev => [newClass, ...prev]);
        setShowCreateModal(false);
        alert(t('messages.supportClassCreated'));
      } else {
        const error = await response.json();
        alert(`Error creating support class: ${error.message}`);
      }
    } catch (error) {
      logger.error('Error creating support class:', error);
      alert(t('messages.errorCreating'));
    }
  };

  const handleCancelEdit = () => {
    setShowEditModal(false);
    setEditingSupportClass(null);
  };

  const handleCourseTypeClick = (type) => {
    setSelectedCourseType(type);
  };

  const handleEditCourseTypeImage = (type, event) => {
    event.stopPropagation(); // Prevent triggering the course type selection
    setEditingCourseTypeImage(type);
    setShowImageEditModal(true);
  };

  const handleSaveCourseTypeImage = async (imageUrl) => {
    if (editingCourseTypeImage) {
      try {
        // Save to database
        const response = await fetch(`${API_BASE_URL}/api/course-type-images/course-type-image`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            courseType: editingCourseTypeImage,
            imageUrl: imageUrl
          }),
        });

        if (!response.ok) {
          throw new Error(`Failed to save image: ${response.statusText}`);
        }

        // Update local state
        setCourseTypeImages(prev => ({
          ...prev,
          [editingCourseTypeImage]: imageUrl
        }));
        
        setShowImageEditModal(false);
        setEditingCourseTypeImage(null);
        
        // Show success message
        alert(t('messages.imageUpdated'));
      } catch (error) {
        logger.error('Error saving image:', error);
        alert(`Failed to save image: ${error.message}`);
      }
    }
  };

  const handleCancelImageEdit = () => {
    setShowImageEditModal(false);
    setEditingCourseTypeImage(null);
  };

  const handleBackToTypes = () => {
    setSelectedCourseType(null);
    setSelectedSchoolLevel(null);
    setSelectedYearLevel(null);
    setActiveYearTab(null);
  };

  const handleBackToSchoolLevels = () => {
    setSelectedSchoolLevel(null);
    setSelectedYearLevel(null);
    setActiveYearTab(null);
  };

  const handleBackToYearLevels = () => {
    setSelectedYearLevel(null);
    setActiveYearTab(null);
  };

  const handleYearTabClick = (yearLevel) => {
    setActiveYearTab(yearLevel.id);
    setSelectedYearLevel(yearLevel);
  };

  const loadYearLevels = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/year-levels`);
      if (response.ok) {
        const result = await response.json();
        setYearLevels(result.data);
      }
    } catch (error) {
      logger.error('Error loading year levels:', error);
    }
  };

  const loadYearLevelsBySchoolLevel = async (schoolLevel) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/year-levels/school-level/${schoolLevel}`);
      if (response.ok) {
        const result = await response.json();
        setYearLevels(result.data);
      }
    } catch (error) {
      logger.error('Error loading year levels by school level:', error);
    }
  };

  const loadYearLevelDetails = async (yearLevelId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/year-levels/${yearLevelId}`);
      if (response.ok) {
        const result = await response.json();
        setSubjects(result.data.subjects);
        setEducationalContent(result.data.content);
      }
    } catch (error) {
      logger.error('Error loading year level details:', error);
    }
  };

  const handleSchoolLevelClick = (schoolLevel) => {
    setSelectedSchoolLevel(schoolLevel);
    setSelectedYearLevel(null);
  };

  const handleYearLevelClick = (yearLevel) => {
    setSelectedYearLevel(yearLevel);
  };

  const handleCreateContent = () => {
    if (!selectedYearLevel) return;
    setNewContent(prev => ({
      ...prev,
      year_level_id: selectedYearLevel.id,
      subject_id: subjects[0]?.id || ''
    }));
    setShowCreateContentModal(true);
  };

  const handleSaveContent = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/educational-content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newContent),
      });

      if (response.ok) {
        setShowCreateContentModal(false);
        setNewContent({
          title: '',
          content: '',
          content_type: 'lesson',
          difficulty_level: 'beginner',
          subject_id: '',
          is_published: false
        });
        // Reload content
        loadYearLevelDetails(selectedYearLevel.id);
        alert('Content created successfully!');
      } else {
        const error = await response.json();
        alert(`Error creating content: ${error.message}`);
      }
    } catch (error) {
      logger.error('Error creating content:', error);
      alert('Error creating content. Please try again.');
    }
  };

  const getFilteredSupportClasses = () => {
    if (!selectedCourseType) return supportClasses;
    return supportClasses.filter(sc => sc.type === selectedCourseType);
  };

  const getCourseTypeInfo = (type) => {
    const typeInfo = {
      recorded: {
        title: 'Recorded Courses',
        description: 'Self-paced learning with pre-recorded lessons',
        icon: 'play_circle',
        color: 'blue',
        features: ['24/7 Access', 'Pre-recorded Lessons', 'Self-Paced Learning']
      },
      workshops: {
        title: 'Live Workshops',
        description: 'Interactive live sessions with expert guidance',
        icon: 'live_tv',
        color: 'orange',
        features: ['Live Sessions', 'Expert Guidance', 'Real-time Q&A']
      },
      normal: {
        title: 'Normal Classes',
        description: 'Structured curriculum with regular assessments',
        icon: 'class',
        color: 'green',
        features: ['Structured Curriculum', 'Regular Assessments', 'Progress Tracking']
      }
    };
    return typeInfo[type] || typeInfo.recorded;
  };

  const uploadImageToBackend = async (file) => {
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch(`${IMAGE_SERVICE_URL}/api/upload-image`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        return result.imageUrl;
      } else {
        throw new Error(result.error || 'Upload failed');
      }
    } catch (error) {
      logger.error('Image upload error:', error);
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  const loadSampleData = () => {
    // Sample data for demonstration
    setTeachers([
      { id: 1, name: 'Dr. Sarah Johnson', email: 'sarah.johnson@email.com', subjects: 'Computer Science', status: 'active', courses: 12, earnings: 4500, rating: 4.8 },
      { id: 2, name: 'Prof. Mike Chen', email: 'mike.chen@email.com', subjects: 'Mathematics', status: 'active', courses: 8, earnings: 3200, rating: 4.6 },
      { id: 3, name: 'Dr. Emily Davis', email: 'emily.davis@email.com', subjects: 'English Literature', status: 'pending', courses: 5, earnings: 1800, rating: 4.9 }
    ]);

    setCourses([
      { id: 1, title: 'Advanced JavaScript Patterns', teacher: 'Prof. Mike Chen', category: 'Programming', status: 'published', enrollments: 156, revenue: 4680, rating: 4.7 },
      { id: 2, title: 'Python for Beginners', teacher: 'Dr. Sarah Johnson', category: 'Programming', status: 'published', enrollments: 234, revenue: 7020, rating: 4.8 },
      { id: 3, title: 'Web Design Fundamentals', teacher: 'Dr. Emily Davis', category: 'Design', status: 'pending', enrollments: 0, revenue: 0, rating: 0 }
    ]);

    setStudents([
      { id: 1, name: 'John Smith', email: 'john.smith@email.com', enrolledCourses: 5, totalSpent: 450, lastActive: '2024-01-15', status: 'active' },
      { id: 2, name: 'Maria Garcia', email: 'maria.garcia@email.com', enrolledCourses: 3, totalSpent: 280, lastActive: '2024-01-14', status: 'active' },
      { id: 3, name: 'David Wilson', email: 'david.wilson@email.com', enrolledCourses: 8, totalSpent: 720, lastActive: '2024-01-10', status: 'inactive' }
    ]);

    setTransactions([
      { id: 1, date: '2024-01-15', student: 'John Smith', course: 'Advanced JavaScript Patterns', teacher: 'Prof. Mike Chen', amount: 89.99, status: 'completed' },
      { id: 2, date: '2024-01-14', student: 'Maria Garcia', course: 'Python for Beginners', teacher: 'Dr. Sarah Johnson', amount: 79.99, status: 'completed' },
      { id: 3, date: '2024-01-13', student: 'David Wilson', course: 'Web Design Fundamentals', teacher: 'Dr. Emily Davis', amount: 69.99, status: 'pending' }
    ]);

    setReviews([
      { id: 1, student: 'John Smith', course: 'Advanced JavaScript Patterns', rating: 5, content: 'Excellent course with practical examples!', status: 'approved' },
      { id: 2, student: 'Maria Garcia', course: 'Python for Beginners', rating: 4, content: 'Great for beginners, very well explained.', status: 'approved' },
      { id: 3, student: 'David Wilson', course: 'Web Design Fundamentals', rating: 5, content: 'Amazing course, learned a lot!', status: 'pending' }
    ]);

    setSettings({
      defaultCommission: 30,
      minPayout: 50,
      autoApproveCourses: false,
      requireVideoPreview: true,
      enableReviews: true,
      enableForums: false,
      enableCertificates: true,
      emailNewCourses: true,
      emailReviews: true
    });

    setSupportClasses([
      { 
        id: 1, 
        type: 'recorded', 
        title: 'Recorded Courses', 
        description: 'Self-paced learning with pre-recorded lessons',
        schoolLevel: 'Primary', 
        subject: 'English', 
        year: 'Grade 1', 
        status: 'active', 
        enrollments: 45, 
        revenue: 1350, 
        rating: 4.8,
        thumbnail: '/assets/images/recorded_courses.jpg'
      },
      { 
        id: 2, 
        type: 'workshops', 
        title: 'Live Workshops', 
        description: 'Interactive live sessions with expert guidance',
        schoolLevel: 'Middle', 
        subject: 'Math', 
        year: 'Grade 2', 
        status: 'active', 
        enrollments: 32, 
        revenue: 960, 
        rating: 4.9,
        thumbnail: '/assets/images/workshops.jpg'
      },
      { 
        id: 3, 
        type: 'normal', 
        title: 'Normal Classes', 
        description: 'Structured curriculum with regular assessments',
        schoolLevel: 'High', 
        subject: 'Physics', 
        year: 'Grade 1', 
        status: 'pending', 
        enrollments: 0, 
        revenue: 0, 
        rating: 0,
        thumbnail: '/assets/images/normal_classes.jpg'
      }
    ]);
  };

  const renderDashboard = () => (
    <div className={styles.dashboardContent}>
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <span className="material-icons">school</span>
          </div>
          <div className={styles.statContent}>
            <h3>{t('stats.totalTeachers')}</h3>
            <p className={styles.statNumber}>247</p>
            <span className={`${styles.statChange} ${styles.positive}`}>+12% {t('stats.thisMonth')}</span>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <span className="material-icons">video_library</span>
          </div>
          <div className={styles.statContent}>
            <h3>{t('stats.activeCourses')}</h3>
            <p className={styles.statNumber}>1,234</p>
            <span className={`${styles.statChange} ${styles.positive}`}>+8% {t('stats.thisMonth')}</span>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <span className="material-icons">people</span>
          </div>
          <div className={styles.statContent}>
            <h3>{t('stats.totalStudents')}</h3>
            <p className={styles.statNumber}>45,678</p>
            <span className={`${styles.statChange} ${styles.positive}`}>+15% {t('stats.thisMonth')}</span>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <span className="material-icons">payments</span>
          </div>
          <div className={styles.statContent}>
            <h3>{t('stats.monthlyRevenue')}</h3>
            <p className={styles.statNumber}>$89,432</p>
            <span className={`${styles.statChange} ${styles.positive}`}>+23% {t('stats.thisMonth')}</span>
          </div>
        </div>
      </div>

      <div className={styles.quickActions}>
        <h2>{t('quickActions.title')}</h2>
        <div className={styles.actionButtons}>
          <button className={styles.actionBtn} onClick={() => setActiveTab('teachers')}>
            <span className="material-icons">person_add</span>
            <span>{t('quickActions.approveTeachers')}</span>
          </button>
          <button className={styles.actionBtn} onClick={() => setActiveTab('courses')}>
            <span className="material-icons">add_circle</span>
            <span>{t('quickActions.reviewCourses')}</span>
          </button>
          <button className={styles.actionBtn} onClick={() => setActiveTab('payments')}>
            <span className="material-icons">account_balance</span>
            <span>{t('quickActions.processPayouts')}</span>
          </button>
          <button className={styles.actionBtn} onClick={() => setActiveTab('reviews')}>
            <span className="material-icons">rate_review</span>
            <span>{t('quickActions.moderateReviews')}</span>
          </button>
        </div>
      </div>

      <div className={styles.recentActivity}>
        <h2>{t('recentActivity.title')}</h2>
        <div className={styles.activityList}>
          <div className={styles.activityItem}>
            <div className={styles.activityIcon}>
              <span className="material-icons">person_add</span>
            </div>
            <div className={styles.activityContent}>
              <p><strong>{t('recentActivity.newTeacherApplication', { name: 'Dr. Sarah Johnson', subject: 'Computer Science' })}</strong></p>
              <span className={styles.activityTime}>{t('recentActivity.hoursAgo', { count: 2 })}</span>
            </div>
            <button className={`${styles.btn} ${styles.btnSm} ${styles.btnPrimary}`}>{t('recentActivity.review')}</button>
          </div>
          <div className={styles.activityItem}>
            <div className={styles.activityIcon}>
              <span className="material-icons">video_library</span>
            </div>
            <div className={styles.activityContent}>
              <p><strong>{t('recentActivity.newCourseSubmitted', { title: 'Advanced JavaScript Patterns', teacher: 'Prof. Mike Chen' })}</strong></p>
              <span className={styles.activityTime}>{t('recentActivity.hoursAgo', { count: 4 })}</span>
            </div>
            <button className={`${styles.btn} ${styles.btnSm} ${styles.btnPrimary}`}>{t('recentActivity.review')}</button>
          </div>
          <div className={styles.activityItem}>
            <div className={styles.activityIcon}>
              <span className="material-icons">star_rate</span>
            </div>
            <div className={styles.activityContent}>
              <p><strong>{t('recentActivity.newReview', { rating: 5, course: 'Python for Beginners' })}</strong></p>
              <span className={styles.activityTime}>{t('recentActivity.hoursAgo', { count: 6 })}</span>
            </div>
            <button className={`${styles.btn} ${styles.btnSm} ${styles.btnPrimary}`}>{t('recentActivity.view')}</button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTeachers = () => (
    <div className={styles.teachersContent}>
      <div className={styles.filtersBar}>
        <div className={styles.searchBox}>
          <span className="material-icons">search</span>
          <input type="text" placeholder="Search teachers..." />
        </div>
        <div className={styles.filterGroup}>
          <select>
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="suspended">Suspended</option>
          </select>
          <select>
            <option value="name">Sort by Name</option>
            <option value="courses">Sort by Courses</option>
            <option value="earnings">Sort by Earnings</option>
            <option value="rating">Sort by Rating</option>
          </select>
        </div>
      </div>

      <div className={styles.teachersGrid}>
        {teachers.map(teacher => (
          <div key={teacher.id} className={styles.teacherCard}>
            <div className={styles.cardHeader}>
              <span className="material-icons">school</span>
              <h3 className={styles.teacherName}>{teacher.name}</h3>
            </div>
            <div className={styles.cardContent}>
              <div><strong>Email:</strong> {teacher.email}</div>
              <div><strong>Subjects:</strong> {teacher.subjects}</div>
              <div><strong>Courses:</strong> {teacher.courses}</div>
              <div><strong>Earnings:</strong> ${teacher.earnings}</div>
              <div><strong>Rating:</strong> {teacher.rating}/5</div>
            </div>
            <div className={styles.cardActions}>
              <button className={`${styles.btn} ${styles.btnSm} ${styles.btnPrimary}`}>Edit</button>
              <button className={`${styles.btn} ${styles.btnSm} ${styles.btnSecondary}`}>View</button>
              <button className={`${styles.btn} ${styles.btnSm} ${styles.btnSecondary}`}>
                {teacher.status === 'active' ? 'Suspend' : 'Activate'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Handle thumbnail file upload
  const handleThumbnailUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    setThumbnailFile(file);
    setUploadingThumbnail(true);

    try {
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result);
        setUploadingThumbnail(false);
      };
      reader.readAsDataURL(file);

      // Here you would upload to your server
      // For now, we'll use the data URL or you can implement actual upload
      // Example: const uploadedUrl = await uploadImageToServer(file);
      // setNewCourse({...newCourse, thumbnail_url: uploadedUrl});
    } catch (error) {
      console.error('Error uploading thumbnail:', error);
      alert('Failed to upload thumbnail image');
      setUploadingThumbnail(false);
    }
  };

  // Handle cover image file upload
  const handleCoverUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    setCoverFile(file);
    setUploadingCover(true);

    try {
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverPreview(reader.result);
        setUploadingCover(false);
      };
      reader.readAsDataURL(file);

      // Here you would upload to your server
      // For now, we'll use the data URL or you can implement actual upload
    } catch (error) {
      console.error('Error uploading cover image:', error);
      alert('Failed to upload cover image');
      setUploadingCover(false);
    }
  };

  // Remove thumbnail
  const handleRemoveThumbnail = () => {
    setThumbnailFile(null);
    setThumbnailPreview(null);
    setNewCourse({...newCourse, thumbnail_url: ''});
  };

  // Remove cover image
  const handleRemoveCover = () => {
    setCoverFile(null);
    setCoverPreview(null);
    setNewCourse({...newCourse, cover_image_url: ''});
  };

  // Handle course video upload
  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('video/')) {
      alert('Please select a video file');
      return;
    }

    // Validate file size (max 500MB)
    if (file.size > 500 * 1024 * 1024) {
      alert('Video size must be less than 500MB');
      return;
    }

    setUploadingVideo(true);

    // Create video object outside try block so it's accessible in catch
    const videoData = {
      id: Date.now(),
      title: file.name.replace(/\.[^/.]+$/, ''), // Remove extension
      file: file,
      url: null, // Will be set after upload
      duration: 0, // Can be extracted from video metadata
      size: file.size,
      type: file.type,
      status: 'uploading'
    };

    try {
      // Add video to list first (with uploading status)
      setCourseVideos([...courseVideos, videoData]);

      // Upload to server
      const formData = new FormData();
      formData.append('video', file);

      const response = await fetch(`${API_BASE_URL}/api/admin-course-videos/upload`, {
        method: 'POST',
        headers: getAdminPanelAuthHeaders(),
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Upload failed');
      }

      const data = await response.json();

      // Update video with server URL
      setCourseVideos(prev => prev.map(v => 
        v.id === videoData.id 
          ? {
              ...v, 
              url: `${API_BASE_URL}${data.file.url}`,
              serverFilename: data.file.filename,
              serverPath: data.file.path,
              status: 'ready'
            }
          : v
      ));
      setUploadingVideo(false);

    } catch (error) {
      console.error('Error uploading video:', error);
      alert(`Failed to upload video: ${error.message}`);
      // Remove video from list on error
      setCourseVideos(prev => prev.filter(v => v.id !== videoData.id));
      setUploadingVideo(false);
    }
  };

  // Remove video
  const handleRemoveVideo = (videoId) => {
    setCourseVideos(courseVideos.filter(v => v.id !== videoId));
  };

  // Update video title
  const handleVideoTitleChange = (videoId, title) => {
    setCourseVideos(courseVideos.map(v => 
      v.id === videoId ? {...v, title} : v
    ));
  };

  // Handle edit course
  const handleEditCourse = (course) => {
    setEditingCourse(course);
    setNewCourse({
      title: course.title || '',
      category: course.category || '',
      price: course.price || '',
      original_price: course.original_price || '',
      level: course.level || 'beginner',
      description: course.description || '',
      teacher_id: course.teacher_id || '',
      duration_minutes: course.duration_minutes || 0,
      thumbnail_url: course.thumbnail_url || '',
      cover_image_url: course.cover_image_url || '',
      status: course.status || 'active'
    });
    setActiveTab('add-course');
    setShowEditCourseModal(true);
  };

  // Handle view course
  const handleViewCourse = async (course) => {
    try {
      // Fetch full course details
      const response = await fetch(`${API_BASE_URL}/api/admin-course-management/courses/${course.id}`, {
        headers: getAdminPanelAuthHeaders(),
      });
      if (response.ok) {
        const courseData = await response.json();
        setViewingCourse(courseData);
        setShowViewCourseModal(true);
      } else {
        alert('Failed to load course details');
      }
    } catch (error) {
      console.error('Error viewing course:', error);
      alert('Error loading course details');
    }
  };

  // Handle toggle course status
  const handleDeleteCourse = async (course) => {
    const confirmMessage = `Are you sure you want to delete "${course.title}"?\n\nThis action cannot be undone and will permanently remove:\n- The course\n- All associated videos\n- All enrollments\n- All reviews`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin-course-management/courses/${course.id}`, {
        method: 'DELETE',
        headers: getAdminPanelAuthHeaders(true),
      });

      const data = await response.json();

      if (response.ok) {
        alert(`Course "${course.title}" deleted successfully`);
        // Reload courses
        loadCourses();
      } else {
        alert(`Error deleting course: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting course:', error);
      alert('Failed to delete course. Please try again.');
    }
  };

  const handleToggleCourseStatus = async (course) => {
    const newStatus = course.status === 'active' ? 'inactive' : 'active';
    const confirmMessage = newStatus === 'active' 
      ? `Activate "${course.title}"?` 
      : `Deactivate "${course.title}"?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin-course-management/courses/${course.id}`, {
        method: 'PUT',
        headers: getAdminPanelAuthHeaders(true),
        body: JSON.stringify({
          status: newStatus
        }),
      });

      if (response.ok) {
        alert(`Course ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully!`);
        loadCourses(); // Reload courses list
      } else {
        const error = await response.json();
        alert(`Error: ${error.error || 'Failed to update course status'}`);
      }
    } catch (error) {
      console.error('Error updating course status:', error);
      alert('Failed to update course status');
    }
  };

  // Handle update course
  const handleUpdateCourse = async (e) => {
    e.preventDefault();
    if (!editingCourse) return;

    setIsCreatingCourse(true);

    try {
      const thumbnailUrl = thumbnailPreview || newCourse.thumbnail_url || null;
      const coverUrl = coverPreview || newCourse.cover_image_url || null;

      const courseData = {
        title: newCourse.title,
        category: newCourse.category,
        price: parseFloat(newCourse.price),
        original_price: newCourse.original_price ? parseFloat(newCourse.original_price) : null,
        level: newCourse.level,
        description: newCourse.description,
        teacher_id: newCourse.teacher_id || null,
        duration_minutes: parseInt(newCourse.duration_minutes) || 0,
        thumbnail_url: thumbnailUrl,
        cover_image_url: coverUrl,
        status: newCourse.status || 'active'
      };

      const response = await fetch(`${API_BASE_URL}/api/admin-course-management/courses/${editingCourse.id}`, {
        method: 'PUT',
        headers: getAdminPanelAuthHeaders(true),
        body: JSON.stringify(courseData),
      });

      if (response.ok) {
        alert('Course updated successfully!');
        // Reset form
        setNewCourse({
          title: '',
          category: '',
          price: '',
          original_price: '',
          level: 'beginner',
          description: '',
          teacher_id: '',
          duration_minutes: 0,
          thumbnail_url: '',
          cover_image_url: '',
          status: 'active'
        });
        setThumbnailFile(null);
        setThumbnailPreview(null);
        setCoverFile(null);
        setCoverPreview(null);
        setCourseVideos([]);
        setEditingCourse(null);
        setShowEditCourseModal(false);
        // Reload courses
        loadCourses();
        // Switch to courses tab
        setActiveTab('courses');
      } else {
        const error = await response.json();
        alert(`Error: ${error.error || 'Failed to update course'}`);
      }
    } catch (error) {
      console.error('Error updating course:', error);
      alert('Failed to update course. Please try again.');
    } finally {
      setIsCreatingCourse(false);
    }
  };

  const handleCreateCourse = async (e) => {
    e.preventDefault();
    setIsCreatingCourse(true);

    try {
      // Use preview URL if file was uploaded, otherwise use URL input
      const thumbnailUrl = thumbnailPreview || newCourse.thumbnail_url || null;
      const coverUrl = coverPreview || newCourse.cover_image_url || null;

      const courseData = {
        title: newCourse.title,
        category: newCourse.category,
        price: parseFloat(newCourse.price),
        original_price: newCourse.original_price ? parseFloat(newCourse.original_price) : null,
        level: newCourse.level,
        description: newCourse.description,
        teacher_id: newCourse.teacher_id || null,
        duration_minutes: parseInt(newCourse.duration_minutes) || 0,
        thumbnail_url: thumbnailUrl,
        cover_image_url: coverUrl,
        status: newCourse.status || 'active'
      };

      const response = await fetch(`${API_BASE_URL}/api/admin-course-management/courses`, {
        method: 'POST',
        headers: getAdminPanelAuthHeaders(true),
        body: JSON.stringify(courseData),
      });

      if (response.ok) {
        const result = await response.json();
        const courseId = result.courseId;

        // Save videos to course
        if (courseVideos.length > 0) {
          try {
            for (let i = 0; i < courseVideos.length; i++) {
              const video = courseVideos[i];
              if (video.status === 'ready' && video.serverPath) {
                await fetch(`${API_BASE_URL}/api/admin-course-videos/${courseId}`, {
                  method: 'POST',
                  headers: getAdminPanelAuthHeaders(true),
                  body: JSON.stringify({
                    video_title: video.title,
                    video_url: video.url,
                    video_path: video.serverPath,
                    file_size: video.size,
                    file_type: video.type,
                    order_index: i + 1
                  }),
                });
              }
            }
          } catch (videoError) {
            console.error('Error saving videos to course:', videoError);
            // Course is created, but videos might not be saved
            alert('Course created, but some videos may not have been saved. Please check.');
          }
        }

        alert('Course created successfully!');
        // Reset form
        setNewCourse({
          title: '',
          category: '',
          price: '',
          original_price: '',
          level: 'beginner',
          description: '',
          teacher_id: '',
          duration_minutes: 0,
          thumbnail_url: '',
          cover_image_url: '',
          status: 'active'
        });
        setThumbnailFile(null);
        setThumbnailPreview(null);
        setCoverFile(null);
        setCoverPreview(null);
        setCourseVideos([]);
        // Reload courses
        loadCourses();
        // Switch to courses tab
        setActiveTab('courses');
      } else {
        const error = await response.json();
        alert(`Error creating course: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      logger.error('Error creating course:', error);
      alert('Failed to create course. Please try again.');
    } finally {
      setIsCreatingCourse(false);
    }
  };

  const renderCourses = () => (
    <div className={styles.coursesContent}>
      <div className={styles.filtersBar}>
        <div className={styles.searchBox}>
          <span className="material-icons">search</span>
          <input type="text" placeholder="Search courses..." />
        </div>
        <div className={styles.filterGroup}>
          <select>
            <option value="">All Categories</option>
            <option value="programming">Programming</option>
            <option value="design">Design</option>
            <option value="business">Business</option>
            <option value="marketing">Marketing</option>
            <option value="languages">Languages</option>
          </select>
          <select>
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="draft">Draft</option>
            <option value="inactive">Inactive</option>
          </select>
          <select>
            <option value="title">Sort by Title</option>
            <option value="enrollments">Sort by Enrollments</option>
            <option value="rating">Sort by Rating</option>
          </select>
        </div>
        <button 
          className={`${styles.btn} ${styles.btnPrimary}`}
          onClick={() => setActiveTab('add-course')}
        >
          <span className="material-icons">add</span>
          Create Course
        </button>
      </div>

      <div className={styles.coursesGrid}>
        {courses.length > 0 ? (
          courses.map(course => (
            <div key={course.id} className={styles.courseCard}>
              {/* Cover Image - Full Width */}
              {course.cover_image_url && (
                <div className={styles.courseCoverImage}>
                  <img src={course.cover_image_url} alt={course.title} />
                </div>
              )}
              {/* Title Below Image */}
              <div className={styles.cardHeader}>
                <h3 className={styles.courseTitle}>{course.title}</h3>
              </div>
              <div className={styles.cardContent}>
                <div><strong>Instructor:</strong> {course.instructor_name || 'Admin'}</div>
                <div><strong>Category:</strong> {course.category}</div>
                <div><strong>Level:</strong> {course.level}</div>
                <div><strong>Duration:</strong> {course.duration || 'N/A'}</div>
                <div><strong>Price:</strong> ${course.price}{course.original_price ? ` (was $${course.original_price})` : ''}</div>
                <div><strong>Enrollments:</strong> {course.students_enrolled || 0}</div>
                <div><strong>Rating:</strong> {course.avg_rating || 0}/5</div>
                <div><strong>Status:</strong> {course.status}</div>
              </div>
              <div className={styles.cardActions}>
                <button 
                  className={`${styles.btn} ${styles.btnSm} ${styles.btnPrimary}`}
                  onClick={() => handleEditCourse(course)}
                >
                  Edit
                </button>
                <button 
                  className={`${styles.btn} ${styles.btnSm} ${styles.btnSecondary}`}
                  onClick={() => handleViewCourse(course)}
                >
                  View
                </button>
                <button 
                  className={`${styles.btn} ${styles.btnSm} ${styles.btnSecondary}`}
                  onClick={() => handleToggleCourseStatus(course)}
                >
                  {course.status === 'active' ? 'Deactivate' : 'Activate'}
                </button>
                <button 
                  className={`${styles.btn} ${styles.btnSm} ${styles.btnDanger}`}
                  onClick={() => handleDeleteCourse(course)}
                  title="Delete course permanently"
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className={styles.emptyState}>
            <span className="material-icons">video_library</span>
            <h3>No courses found</h3>
            <p>Create your first course to get started.</p>
            <button 
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={() => setActiveTab('add-course')}
            >
              <span className="material-icons">add</span>
              Create Course
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const renderStudents = () => (
    <div className={styles.studentsContent}>
      <div className={styles.filtersBar}>
        <div className={styles.searchBox}>
          <span className="material-icons">search</span>
          <input type="text" placeholder="Search students..." />
        </div>
        <div className={styles.filterGroup}>
          <select>
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>
      </div>

      <div className={styles.studentsTableContainer}>
        <table className={styles.studentsTable}>
          <thead>
            <tr>
              <th>Student</th>
              <th>Email</th>
              <th>Enrolled Courses</th>
              <th>Total Spent</th>
              <th>Last Active</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {students.map(student => (
              <tr key={student.id}>
                <td>{student.name}</td>
                <td>{student.email}</td>
                <td>{student.enrolledCourses}</td>
                <td>${student.totalSpent}</td>
                <td>{student.lastActive}</td>
                <td>
                  <span className={`${styles.statusBadge} ${styles[`status${student.status.charAt(0).toUpperCase() + student.status.slice(1)}`]}`}>
                    {student.status}
                  </span>
                </td>
                <td>
                  <button className={`${styles.btn} ${styles.btnSm} ${styles.btnSecondary}`}>View</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderPayments = () => (
    <div className={styles.paymentsContent}>
      <div className={styles.revenueOverview}>
        <div className={styles.revenueCard}>
          <h3>Total Revenue</h3>
          <p className={styles.revenueAmount}>$1,234,567</p>
          <span className={styles.revenuePeriod}>This Year</span>
        </div>
        <div className={styles.revenueCard}>
          <h3>Monthly Revenue</h3>
          <p className={styles.revenueAmount}>$89,432</p>
          <span className={styles.revenuePeriod}>This Month</span>
        </div>
        <div className={styles.revenueCard}>
          <h3>Pending Payouts</h3>
          <p className={styles.revenueAmount}>$45,678</p>
          <span className={styles.revenuePeriod}>To Teachers</span>
        </div>
        <div className={styles.revenueCard}>
          <h3>Platform Fee</h3>
          <p className={styles.revenueAmount}>$12,345</p>
          <span className={styles.revenuePeriod}>This Month</span>
        </div>
      </div>

      <div className={styles.transactionsSection}>
        <h2>Recent Transactions</h2>
        <div className={styles.transactionsTableContainer}>
          <table className={styles.transactionsTable}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Student</th>
                <th>Course</th>
                <th>Teacher</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map(transaction => (
                <tr key={transaction.id}>
                  <td>{transaction.date}</td>
                  <td>{transaction.student}</td>
                  <td>{transaction.course}</td>
                  <td>{transaction.teacher}</td>
                  <td>${transaction.amount}</td>
                  <td>
                    <span className={`${styles.statusBadge} ${styles[`status${transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}`]}`}>
                      {transaction.status}
                    </span>
                  </td>
                  <td>
                    <button className={`${styles.btn} ${styles.btnSm} ${styles.btnSecondary}`}>View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderReviews = () => (
    <div className={styles.reviewsContent}>
      <div className={styles.filtersBar}>
        <div className={styles.searchBox}>
          <span className="material-icons">search</span>
          <input type="text" placeholder="Search reviews..." />
        </div>
        <div className={styles.filterGroup}>
          <select>
            <option value="">All Ratings</option>
            <option value="5">5 Stars</option>
            <option value="4">4 Stars</option>
            <option value="3">3 Stars</option>
            <option value="2">2 Stars</option>
            <option value="1">1 Star</option>
          </select>
          <select>
            <option value="">All Status</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
            <option value="hidden">Hidden</option>
          </select>
        </div>
      </div>

      <div className={styles.reviewsList}>
        {reviews.map(review => (
          <div key={review.id} className={styles.reviewCard}>
            <div className={styles.reviewHeader}>
              <div className={styles.reviewInfo}>
                <strong>{review.student}</strong>
                <div className={styles.reviewRating}>
                  {'★'.repeat(review.rating)}
                </div>
              </div>
              <span className={`${styles.statusBadge} ${styles[`status${review.status.charAt(0).toUpperCase() + review.status.slice(1)}`]}`}>
                {review.status}
              </span>
            </div>
            <div className={styles.reviewContent}>
              <p><strong>Course:</strong> {review.course}</p>
              <p>{review.content}</p>
            </div>
            <div className={styles.reviewActions}>
              <button className={`${styles.btn} ${styles.btnSm} ${styles.btnPrimary}`}>Approve</button>
              <button className={`${styles.btn} ${styles.btnSm} ${styles.btnSecondary}`}>Hide</button>
              <button className={`${styles.btn} ${styles.btnSm} ${styles.btnSecondary}`}>Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderAnalytics = () => (
    <div className={styles.analyticsContent}>
      <div className={styles.analyticsGrid}>
        <div className={styles.chartContainer}>
          <h3>Revenue Trend</h3>
          <div className={styles.chartPlaceholder}>
            <span className="material-icons">show_chart</span>
            <p>Revenue chart will be displayed here</p>
          </div>
        </div>
        <div className={styles.chartContainer}>
          <h3>Course Enrollments</h3>
          <div className={styles.chartPlaceholder}>
            <span className="material-icons">bar_chart</span>
            <p>Enrollments chart will be displayed here</p>
          </div>
        </div>
        <div className={styles.chartContainer}>
          <h3>Top Categories</h3>
          <div className={styles.chartPlaceholder}>
            <span className="material-icons">pie_chart</span>
            <p>Categories chart will be displayed here</p>
          </div>
        </div>
        <div className={styles.chartContainer}>
          <h3>Teacher Performance</h3>
          <div className={styles.chartPlaceholder}>
            <span className="material-icons">trending_up</span>
            <p>Teacher performance chart will be displayed here</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSettings = () => (
    <div className={styles.settingsContent}>
      <div className={styles.settingsGrid}>
        <div className={styles.settingsCard}>
          <h3>Commission Settings</h3>
          <div className={styles.settingItem}>
            <label htmlFor="defaultCommission">Default Commission Rate (%)</label>
            <input type="number" name="defaultCommission" value={settings.defaultCommission} min="0" max="100" />
          </div>
          <div className={styles.settingItem}>
            <label htmlFor="minPayout">Minimum Payout Amount ($)</label>
            <input type="number" name="minPayout" value={settings.minPayout} min="0" />
          </div>
        </div>

        <div className={styles.settingsCard}>
          <h3>Course Approval</h3>
          <div className={styles.settingItem}>
            <label htmlFor="autoApproveCourses">Auto-approve courses</label>
            <input type="checkbox" name="autoApproveCourses" checked={settings.autoApproveCourses} />
          </div>
          <div className={styles.settingItem}>
            <label htmlFor="requireVideoPreview">Require video preview</label>
            <input type="checkbox" name="requireVideoPreview" checked={settings.requireVideoPreview} />
          </div>
        </div>

        <div className={styles.settingsCard}>
          <h3>Platform Features</h3>
          <div className={styles.settingItem}>
            <label htmlFor="enableReviews">Enable course reviews</label>
            <input type="checkbox" name="enableReviews" checked={settings.enableReviews} />
          </div>
          <div className={styles.settingItem}>
            <label htmlFor="enableForums">Enable discussion forums</label>
            <input type="checkbox" name="enableForums" checked={settings.enableForums} />
          </div>
          <div className={styles.settingItem}>
            <label htmlFor="enableCertificates">Enable course certificates</label>
            <input type="checkbox" name="enableCertificates" checked={settings.enableCertificates} />
          </div>
        </div>

        <div className={styles.settingsCard}>
          <h3>Notifications</h3>
          <div className={styles.settingItem}>
            <label htmlFor="emailNewCourses">Email notifications for new courses</label>
            <input type="checkbox" name="emailNewCourses" checked={settings.emailNewCourses} />
          </div>
          <div className={styles.settingItem}>
            <label htmlFor="emailReviews">Email notifications for reviews</label>
            <input type="checkbox" name="emailReviews" checked={settings.emailReviews} />
          </div>
        </div>
      </div>

      <div className={styles.settingsActions}>
        <button type="button" className={`${styles.btn} ${styles.btnPrimary}`}>Save Settings</button>
        <button type="button" className={`${styles.btn} ${styles.btnSecondary}`}>Reset to Default</button>
      </div>
    </div>
  );

  const renderCreateModal = () => {
    if (!showCreateModal) return null;

    const handleCreateSubmit = (e) => {
      e.preventDefault();
      if (!newSupportClass.title || !newSupportClass.subject || !newSupportClass.year) {
        alert('Please fill in all required fields');
        return;
      }
      handleSaveNewSupportClass(newSupportClass);
    };

    return (
      <div className={styles.modalOverlay} onClick={handleCancelCreate}>
        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <div>
              <h3>Create New Support Class</h3>
              <div className={styles.backendStatus}>
                <span className={`${styles.statusIndicator} ${styles[backendStatus]}`}>
                  <span className="material-icons">
                    {backendStatus === 'online' ? 'cloud_done' : 
                     backendStatus === 'offline' ? 'cloud_off' : 'hourglass_empty'}
                  </span>
                  {backendStatus === 'online' ? 'Backend Online' : 
                   backendStatus === 'offline' ? 'Backend Offline' : 'Checking...'}
                </span>
              </div>
            </div>
            <button className={styles.closeButton} onClick={handleCancelCreate}>
              <span className="material-icons">close</span>
            </button>
          </div>
          
          <form onSubmit={handleCreateSubmit}>
            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label>Title *</label>
                <input 
                  type="text" 
                  value={newSupportClass.title}
                  onChange={(e) => setNewSupportClass(prev => ({
                    ...prev,
                    title: e.target.value
                  }))}
                  className={styles.formInput}
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label>Description</label>
                <textarea 
                  value={newSupportClass.description}
                  onChange={(e) => setNewSupportClass(prev => ({
                    ...prev,
                    description: e.target.value
                  }))}
                  className={styles.formTextarea}
                  rows="3"
                />
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Type *</label>
                  <select 
                    value={newSupportClass.type}
                    onChange={(e) => setNewSupportClass(prev => ({
                      ...prev,
                      type: e.target.value
                    }))}
                    className={styles.formSelect}
                    required
                  >
                    <option value="recorded">Recorded Courses</option>
                    <option value="workshops">Workshops</option>
                    <option value="normal">Normal Classes</option>
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label>School Level *</label>
                  <select 
                    value={newSupportClass.school_level}
                    onChange={(e) => setNewSupportClass(prev => ({
                      ...prev,
                      school_level: e.target.value
                    }))}
                    className={styles.formSelect}
                    required
                  >
                    <option value="primary">Primary</option>
                    <option value="middle">Middle</option>
                    <option value="high">High</option>
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label>Teacher *</label>
                  <select 
                    value={newSupportClass.teacher_id}
                    onChange={(e) => setNewSupportClass(prev => ({
                      ...prev,
                      teacher_id: parseInt(e.target.value)
                    }))}
                    className={styles.formSelect}
                    required
                  >
                    {teachers.map(teacher => (
                      <option key={teacher.id} value={teacher.id}>{teacher.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Subject *</label>
                  <input 
                    type="text" 
                    value={newSupportClass.subject}
                    onChange={(e) => setNewSupportClass(prev => ({
                      ...prev,
                      subject: e.target.value
                    }))}
                    className={styles.formInput}
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Year *</label>
                  <input 
                    type="text" 
                    value={newSupportClass.year}
                    onChange={(e) => setNewSupportClass(prev => ({
                      ...prev,
                      year: e.target.value
                    }))}
                    className={styles.formInput}
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Price ($)</label>
                  <input 
                    type="number" 
                    value={newSupportClass.price}
                    onChange={(e) => setNewSupportClass(prev => ({
                      ...prev,
                      price: parseFloat(e.target.value) || 0
                    }))}
                    className={styles.formInput}
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              {newSupportClass.type === 'workshops' && (
                <div className={styles.formGroup}>
                  <label>Enrollment Target</label>
                  <input 
                    type="number" 
                    value={newSupportClass.enrollment_target}
                    onChange={(e) => setNewSupportClass(prev => ({
                      ...prev,
                      enrollment_target: parseInt(e.target.value) || 0
                    }))}
                    className={styles.formInput}
                    min="0"
                    placeholder="Minimum students needed to start workshop"
                  />
                </div>
              )}

              <div className={styles.formGroup}>
                <label>Cover Image URL</label>
                <input 
                  type="url" 
                  value={newSupportClass.cover_image_url}
                  onChange={(e) => setNewSupportClass(prev => ({
                    ...prev,
                    cover_image_url: e.target.value
                  }))}
                  className={styles.formInput}
                  placeholder="https://example.com/image.jpg"
                />
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button 
                type="button"
                className={`${styles.btn} ${styles.btnSecondary}`}
                onClick={handleCancelCreate}
              >
                Cancel
              </button>
              <button 
                type="submit"
                className={`${styles.btn} ${styles.btnPrimary}`}
              >
                Create Support Class
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const renderEditModal = () => {
    if (!editingSupportClass) return null;

    return (
      <div className={styles.modalOverlay} onClick={handleCancelEdit}>
        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <div>
              <h3>Edit Support Class</h3>
              <div className={styles.backendStatus}>
                <span className={`${styles.statusIndicator} ${styles[backendStatus]}`}>
                  <span className="material-icons">
                    {backendStatus === 'online' ? 'cloud_done' : 
                     backendStatus === 'offline' ? 'cloud_off' : 'hourglass_empty'}
                  </span>
                  {backendStatus === 'online' ? 'Backend Online' : 
                   backendStatus === 'offline' ? 'Backend Offline' : 'Checking...'}
                </span>
              </div>
            </div>
            <button className={styles.closeButton} onClick={handleCancelEdit}>
              <span className="material-icons">close</span>
            </button>
          </div>
          
          <div className={styles.modalBody}>
            <div className={styles.formGroup}>
              <label>Cover Image</label>
              <div className={styles.imageUploadSection}>
                <div className={styles.currentImage}>
                  <img src={editingSupportClass.thumbnail} alt="Current cover" />
                </div>
                <div className={styles.uploadOptions}>
                  <input 
                    type="file" 
                    accept="image/*" 
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        // Validate file size (5MB limit)
                        if (file.size > 5 * 1024 * 1024) {
                          alert('File size must be less than 5MB');
                          return;
                        }

                        // Validate file type
                        if (!file.type.startsWith('image/')) {
                          alert('Please select an image file');
                          return;
                        }

                        try {
                          // Upload to backend
                          const imageUrl = await uploadImageToBackend(file);
                          
                          // Update with backend URL
                          setEditingSupportClass(prev => ({
                            ...prev,
                            thumbnail: imageUrl
                          }));
                        } catch (error) {
                          logger.error('Upload failed:', error);
                          alert(`Failed to upload image: ${error.message}`);
                        }
                      }
                    }}
                    style={{ display: 'none' }}
                    id="cover-upload"
                  />
                  <label 
                    htmlFor="cover-upload" 
                    className={`${styles.btn} ${styles.btnSecondary} ${isUploading ? styles.disabled : ''}`}
                    style={{ opacity: isUploading ? 0.6 : 1 }}
                  >
                    <span className="material-icons">
                      {isUploading ? 'hourglass_empty' : 'upload'}
                    </span>
                    {isUploading ? 'Uploading...' : 'Upload New Image'}
                  </label>
                  <button 
                    className={`${styles.btn} ${styles.btnSecondary}`}
                    onClick={() => {
                      const url = prompt('Enter image URL:');
                      if (url) {
                        setEditingSupportClass(prev => ({
                          ...prev,
                          thumbnail: url
                        }));
                      }
                    }}
                  >
                    <span className="material-icons">link</span>
                    Use URL
                  </button>
                </div>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label>Title</label>
              <input 
                type="text" 
                value={editingSupportClass.title}
                onChange={(e) => setEditingSupportClass(prev => ({
                  ...prev,
                  title: e.target.value
                }))}
                className={styles.formInput}
              />
            </div>

            <div className={styles.formGroup}>
              <label>Description</label>
              <textarea 
                value={editingSupportClass.description}
                onChange={(e) => setEditingSupportClass(prev => ({
                  ...prev,
                  description: e.target.value
                }))}
                className={styles.formTextarea}
                rows="3"
              />
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>School Level</label>
                <select 
                  value={editingSupportClass.schoolLevel}
                  onChange={(e) => setEditingSupportClass(prev => ({
                    ...prev,
                    schoolLevel: e.target.value
                  }))}
                  className={styles.formSelect}
                >
                  <option value="Primary">Primary</option>
                  <option value="Middle">Middle</option>
                  <option value="High">High</option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label>Subject</label>
                <input 
                  type="text" 
                  value={editingSupportClass.subject}
                  onChange={(e) => setEditingSupportClass(prev => ({
                    ...prev,
                    subject: e.target.value
                  }))}
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label>Year</label>
                <input 
                  type="text" 
                  value={editingSupportClass.year}
                  onChange={(e) => setEditingSupportClass(prev => ({
                    ...prev,
                    year: e.target.value
                  }))}
                  className={styles.formInput}
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label>Status</label>
              <select 
                value={editingSupportClass.status}
                onChange={(e) => setEditingSupportClass(prev => ({
                  ...prev,
                  status: e.target.value
                }))}
                className={styles.formSelect}
              >
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className={styles.modalFooter}>
            <button 
              className={`${styles.btn} ${styles.btnSecondary}`}
              onClick={handleCancelEdit}
            >
              Cancel
            </button>
            <button 
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={() => handleSaveSupportClass(editingSupportClass)}
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderSupportClasses = () => {
    const schoolLevels = [
      { id: 'primary', name: 'Primary School', icon: 'school', color: 'blue' },
      { id: 'middle', name: 'Middle School', icon: 'school', color: 'green' },
      { id: 'high', name: 'High School', icon: 'school', color: 'orange' }
    ];

    const getSchoolLevelInfo = (schoolLevelId) => {
      return schoolLevels.find(level => level.id === schoolLevelId);
    };

    const getYearLevelsForSchoolLevel = (schoolLevelId) => {
      return yearLevels.filter(level => level.school_level === schoolLevelId);
    };

    const getFilteredContent = () => {
      if (!searchQuery) return educationalContent;
      return educationalContent.filter(content => 
        content.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        content.content.toLowerCase().includes(searchQuery.toLowerCase())
      );
    };
    
    if (!selectedCourseType) {
      // Show course type selection cards
      return (
        <div className={styles.supportClassesContent}>
          <div className={styles.contentHeader}>
            <h2>Support Classes Management</h2>
            <p>Choose a course type to manage</p>
          </div>

          <div className={styles.courseTypeCards}>
            {/* Recorded Courses Card */}
            <div 
              className={`${styles.courseTypeCard} ${styles.recorded}`}
              onClick={() => handleCourseTypeClick('recorded')}
            >
              <div className={styles.courseTypeImageContainer}>
                <img 
                  src={courseTypeImages.recorded} 
                  alt="Recorded Courses" 
                  className={styles.courseTypeImage}
                />
                <button 
                  className={styles.editImageButton}
                  onClick={(e) => handleEditCourseTypeImage('recorded', e)}
                  title="Edit cover image"
                >
                  <span className="material-icons">edit</span>
                </button>
              </div>
              <div className={styles.courseTypeContent}>
                <h3>Recorded Courses</h3>
                <p>Self-paced learning with pre-recorded lessons</p>
                <div className={styles.courseTypeFeatures}>
                  <span>24/7 Access</span>
                  <span>Pre-recorded Lessons</span>
                  <span>Self-Paced Learning</span>
                </div>
                <div className={styles.courseTypeStats}>
                  <span>{supportClasses.filter(sc => sc.type === 'recorded').length} courses</span>
                </div>
              </div>
            </div>

            {/* Workshops Card */}
            <div 
              className={`${styles.courseTypeCard} ${styles.workshops}`}
              onClick={() => handleCourseTypeClick('workshops')}
            >
              <div className={styles.courseTypeImageContainer}>
                <img 
                  src={courseTypeImages.workshops} 
                  alt="Live Workshops" 
                  className={styles.courseTypeImage}
                />
                <button 
                  className={styles.editImageButton}
                  onClick={(e) => handleEditCourseTypeImage('workshops', e)}
                  title="Edit cover image"
                >
                  <span className="material-icons">edit</span>
                </button>
              </div>
              <div className={styles.courseTypeContent}>
                <h3>Live Workshops</h3>
                <p>Interactive live sessions with expert guidance</p>
                <div className={styles.courseTypeFeatures}>
                  <span>Live Sessions</span>
                  <span>Expert Guidance</span>
                  <span>Real-time Q&A</span>
                </div>
                <div className={styles.courseTypeStats}>
                  <span>{supportClasses.filter(sc => sc.type === 'workshops').length} courses</span>
                </div>
              </div>
            </div>

            {/* Normal Classes Card */}
            <div 
              className={`${styles.courseTypeCard} ${styles.normal}`}
              onClick={() => handleCourseTypeClick('normal')}
            >
              <div className={styles.courseTypeImageContainer}>
                <img 
                  src={courseTypeImages.normal} 
                  alt="Normal Classes" 
                  className={styles.courseTypeImage}
                />
                <button 
                  className={styles.editImageButton}
                  onClick={(e) => handleEditCourseTypeImage('normal', e)}
                  title="Edit cover image"
                >
                  <span className="material-icons">edit</span>
                </button>
              </div>
              <div className={styles.courseTypeContent}>
                <h3>Normal Classes</h3>
                <p>Structured curriculum with regular assessments</p>
                <div className={styles.courseTypeFeatures}>
                  <span>Structured Curriculum</span>
                  <span>Regular Assessments</span>
                  <span>Progress Tracking</span>
                </div>
                <div className={styles.courseTypeStats}>
                  <span>{supportClasses.filter(sc => sc.type === 'normal').length} courses</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (!selectedSchoolLevel) {
      // Show school level selection after course type is selected
      const typeInfo = getCourseTypeInfo(selectedCourseType);
      
      return (
        <div className={styles.supportClassesContent}>
          <div className={styles.contentHeader}>
            <button 
              className={`${styles.btn} ${styles.btnSecondary}`}
              onClick={handleBackToTypes}
              style={{ marginRight: '10px' }}
            >
              <span className="material-icons">arrow_back</span>
              Back to Course Types
            </button>
            <h2>{typeInfo.title} Management</h2>
            <p>Select a school level to manage {typeInfo.title.toLowerCase()}</p>
          </div>

          <div className={styles.schoolLevelCards}>
            {schoolLevels.map(schoolLevel => {
              const yearLevelsForThisSchool = getYearLevelsForSchoolLevel(schoolLevel.id);
              return (
                <div 
                  key={schoolLevel.id} 
                  className={`${styles.schoolLevelCard} ${styles[schoolLevel.color]}`}
                  onClick={() => handleSchoolLevelClick(schoolLevel.id)}
                >
                  <div className={styles.schoolLevelIcon}>
                    <span className="material-icons">{schoolLevel.icon}</span>
                  </div>
                  <div className={styles.schoolLevelContent}>
                    <h3>{schoolLevel.name}</h3>
                    <p>Manage {typeInfo.title.toLowerCase()} for {schoolLevel.name.toLowerCase()}</p>
                    <div className={styles.schoolLevelStats}>
                      <span>{yearLevelsForThisSchool.length} year levels</span>
                      <span>{yearLevelsForThisSchool.reduce((sum, yl) => sum + yl.content_count, 0)} content items</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    if (!selectedYearLevel) {
      // Show year level tabs after school level is selected
      const schoolLevelInfo = getSchoolLevelInfo(selectedSchoolLevel);
      const yearLevelsForSchool = getYearLevelsForSchoolLevel(selectedSchoolLevel);
      const typeInfo = getCourseTypeInfo(selectedCourseType);
      
      return (
        <div className={styles.supportClassesContent}>
          <div className={styles.contentHeader}>
            <button 
              className={`${styles.btn} ${styles.btnSecondary}`}
              onClick={handleBackToSchoolLevels}
              style={{ marginRight: '10px' }}
            >
              <span className="material-icons">arrow_back</span>
              Back to School Levels
            </button>
            <h2>{typeInfo.title} Management</h2>
            <p>Select a year level to manage {typeInfo.title.toLowerCase()} content</p>
          </div>

          <div className={styles.yearTabsContainer}>
            {yearLevelsForSchool.length > 0 ? (
              yearLevelsForSchool.map(yearLevel => (
                <button
                  key={yearLevel.id}
                  className={`${styles.yearTab} ${activeYearTab === yearLevel.id ? styles.active : ''}`}
                  onClick={() => handleYearTabClick(yearLevel)}
                >
                  <span className="material-icons">grade</span>
                  <span>{yearLevel.year_name}</span>
                  <div className={styles.yearTabStats}>
                    <span>{yearLevel.content_count || 0} content</span>
                  </div>
                </button>
              ))
            ) : (
              <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                <p>Loading year levels...</p>
              </div>
            )}
          </div>

          {activeYearTab && (
            <div className={styles.yearContentArea}>
              <div className={styles.yearContentHeader}>
                <h3>{yearLevelsForSchool.find(yl => yl.id === activeYearTab)?.year_name} Content</h3>
                <p>Manage educational content for {yearLevelsForSchool.find(yl => yl.id === activeYearTab)?.year_name}</p>
              </div>
              
              <div className={styles.searchSection}>
                <div className={styles.searchBox}>
                  <span className="material-icons">search</span>
                  <input 
                    type="text" 
                    placeholder="Search vocabulary content..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <button 
                  className={`${styles.btn} ${styles.btnPrimary}`}
                  onClick={handleCreateContent}
                >
                  <span className="material-icons">add</span>
                  Add New Content
                </button>
              </div>

              <div className={styles.contentGrid}>
                {educationalContent.map(content => (
                  <div key={content.id} className={styles.contentCard}>
                    <div className={styles.contentHeader}>
                      <h3>{content.title}</h3>
                      <div className={styles.contentMeta}>
                        <span className="material-icons">school</span>
                        <span>{content.subject_name}</span>
                        <span className="material-icons">calendar_today</span>
                        <span>{content.year_name}</span>
                        <span className="material-icons">schedule</span>
                        <span>{new Date(content.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className={styles.contentBody}>
                      <p>{content.content.substring(0, 200)}...</p>
                    </div>
                    <div className={styles.contentActions}>
                      <button className={`${styles.btn} ${styles.btnSecondary}`}>
                        <span className="material-icons">edit</span>
                        Edit
                      </button>
                      <button className={`${styles.btn} ${styles.btnPrimary}`}>
                        <span className="material-icons">visibility</span>
                        View
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {educationalContent.length === 0 && (
                <div className={styles.emptyState}>
                  <span className="material-icons">inbox</span>
                  <h3>No content found</h3>
                  <p>Create your first educational content for this year level.</p>
                  <button 
                    className={`${styles.btn} ${styles.btnPrimary}`}
                    onClick={handleCreateContent}
                  >
                    <span className="material-icons">add</span>
                    Add New Content
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      );
    }

    // Show year level details and content management
    const schoolLevelInfo = getSchoolLevelInfo(selectedYearLevel.school_level);
    const typeInfo = getCourseTypeInfo(selectedCourseType);
    const filteredContent = getFilteredContent();
    
    return (
      <div className={styles.supportClassesContent}>
        <div className={styles.contentHeader}>
          <button 
            className={`${styles.btn} ${styles.btnSecondary}`}
            onClick={handleBackToYearLevels}
            style={{ marginRight: '10px' }}
          >
            <span className="material-icons">arrow_back</span>
            Back to Year Levels
          </button>
          <h2>{selectedYearLevel.year_name} - {schoolLevelInfo.name}</h2>
          <p>Manage educational vocabulary and reading content for {selectedYearLevel.year_name}</p>
          
          <div className={styles.breadcrumb}>
            <span>{typeInfo.title}</span>
            <span className="material-icons">chevron_right</span>
            <span>{schoolLevelInfo.name}</span>
            <span className="material-icons">chevron_right</span>
            <span>{selectedYearLevel.year_name}</span>
          </div>
        </div>

        <div className={styles.searchSection}>
          <div className={styles.searchBox}>
            <span className="material-icons">search</span>
            <input 
              type="text" 
              placeholder="Search vocabulary content..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button 
            className={`${styles.btn} ${styles.btnPrimary}`}
            onClick={handleCreateContent}
          >
            <span className="material-icons">add</span>
            Add New Content
          </button>
        </div>

        <div className={styles.contentGrid}>
          {filteredContent.map(content => (
            <div key={content.id} className={styles.contentCard}>
              <div className={styles.contentHeader}>
                <h3>{content.title}</h3>
                <div className={styles.contentMeta}>
                  <span className="material-icons">school</span>
                  <span>{content.subject_name}</span>
                  <span className="material-icons">calendar_today</span>
                  <span>{content.year_name}</span>
                  <span className="material-icons">schedule</span>
                  <span>{new Date(content.created_at).toLocaleDateString()}</span>
                </div>
              </div>
              <div className={styles.contentBody}>
                <p>{content.content.substring(0, 200)}...</p>
              </div>
              <div className={styles.contentActions}>
                <button className={`${styles.btn} ${styles.btnSecondary}`}>
                  <span className="material-icons">edit</span>
                  Edit
                </button>
                <button className={`${styles.btn} ${styles.btnPrimary}`}>
                  <span className="material-icons">visibility</span>
                  View
                </button>
              </div>
            </div>
          ))}
        </div>

        {filteredContent.length === 0 && (
          <div className={styles.emptyState}>
            <span className="material-icons">inbox</span>
            <h3>No content found</h3>
            <p>Create your first educational content for this year level.</p>
            <button 
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={handleCreateContent}
            >
              <span className="material-icons">add</span>
              Add New Content
            </button>
          </div>
        )}

        {renderCreateContentModal()}
        {renderImageEditModal()}
        
        {/* Simple test modal */}
        {showImageEditModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.8)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div style={{
              background: 'white',
              padding: '20px',
              borderRadius: '10px',
              maxWidth: '500px',
              width: '90%'
            }}>
              <h2>Test Modal - Working!</h2>
              <p>Editing: {editingCourseTypeImage}</p>
              <button 
                onClick={() => {
                  setShowImageEditModal(false);
                  setEditingCourseTypeImage(null);
                }}
                style={{ padding: '10px', background: 'red', color: 'white' }}
              >
                Close Modal
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderImageEditModal = () => {
    if (!showImageEditModal || !editingCourseTypeImage) {
      return null;
    }

    const courseTypeNames = {
      recorded: 'Recorded Courses',
      workshops: 'Live Workshops',
      normal: 'Normal Classes'
    };
    return (
      <div className={styles.modalOverlay} onClick={handleCancelImageEdit}>
        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <h3>Edit {courseTypeNames[editingCourseTypeImage]} Cover Image</h3>
            <div className={styles.backendStatus}>
              <span className={`${styles.statusIndicator} ${styles[backendStatus]}`}>
                <span className="material-icons">
                  {backendStatus === 'online' ? 'cloud_done' : 
                   backendStatus === 'offline' ? 'cloud_off' : 'hourglass_empty'}
                </span>
                {backendStatus === 'online' ? 'Backend Online' : 
                 backendStatus === 'offline' ? 'Backend Offline' : 'Checking...'}
              </span>
            </div>
            <button className={styles.closeButton} onClick={handleCancelImageEdit}>
              <span className="material-icons">close</span>
            </button>
          </div>
          
          <div className={styles.modalBody}>
            <div className={styles.formGroup}>
              <label>Current Cover Image</label>
              <div className={styles.imageUploadSection}>
                <div className={styles.currentImage}>
                  <img src={courseTypeImages[editingCourseTypeImage]} alt="Current cover" />
                </div>
                <div className={styles.uploadOptions}>
                  <input 
                    type="file" 
                    accept="image/*" 
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        // Validate file size (5MB limit)
                        if (file.size > 5 * 1024 * 1024) {
                          alert('File size must be less than 5MB');
                          return;
                        }

                        // Validate file type
                        if (!file.type.startsWith('image/')) {
                          alert('Please select an image file');
                          return;
                        }

                        try {
                          // Upload to backend
                          const imageUrl = await uploadImageToBackend(file);
                          handleSaveCourseTypeImage(imageUrl);
                        } catch (error) {
                          logger.error('Upload failed:', error);
                          alert(`Failed to upload image: ${error.message}`);
                        }
                      }
                    }}
                    style={{ display: 'none' }}
                    id="course-type-upload"
                  />
                  <label 
                    htmlFor="course-type-upload" 
                    className={`${styles.btn} ${styles.btnSecondary} ${isUploading ? styles.disabled : ''}`}
                    style={{ opacity: isUploading ? 0.6 : 1 }}
                  >
                    <span className="material-icons">
                      {isUploading ? 'hourglass_empty' : 'upload'}
                    </span>
                    {isUploading ? 'Uploading...' : 'Upload New Image'}
                  </label>
                  <button 
                    className={`${styles.btn} ${styles.btnSecondary}`}
                    onClick={() => {
                      const url = prompt('Enter image URL:');
                      if (url) {
                        handleSaveCourseTypeImage(url);
                      }
                    }}
                  >
                    <span className="material-icons">link</span>
                    Use URL
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.modalFooter}>
            <button 
              type="button"
              className={`${styles.btn} ${styles.btnSecondary}`}
              onClick={handleCancelImageEdit}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render View Course Modal
  const renderViewCourseModal = () => {
    if (!showViewCourseModal || !viewingCourse) return null;

    return (
      <div className={styles.modalOverlay} onClick={() => setShowViewCourseModal(false)}>
        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
          <div className={styles.modalHeader}>
            <h3>Course Details</h3>
            <button className={styles.closeButton} onClick={() => setShowViewCourseModal(false)}>
              <span className="material-icons">close</span>
            </button>
          </div>
          
          <div className={styles.modalBody}>
            {viewingCourse.cover_image_url && (
              <div className={styles.viewCourseCover}>
                <img src={viewingCourse.cover_image_url} alt={viewingCourse.title} />
              </div>
            )}
            
            <h2 style={{ marginTop: '20px', marginBottom: '16px' }}>{viewingCourse.title}</h2>
            
            <div className={styles.viewCourseDetails}>
              <div className={styles.detailRow}>
                <strong>Category:</strong> <span>{viewingCourse.category}</span>
              </div>
              <div className={styles.detailRow}>
                <strong>Level:</strong> <span>{viewingCourse.level}</span>
              </div>
              <div className={styles.detailRow}>
                <strong>Duration:</strong> <span>{viewingCourse.duration || 'N/A'}</span>
              </div>
              <div className={styles.detailRow}>
                <strong>Price:</strong> <span>${viewingCourse.price}{viewingCourse.original_price ? ` (was $${viewingCourse.original_price})` : ''}</span>
              </div>
              <div className={styles.detailRow}>
                <strong>Instructor:</strong> <span>{viewingCourse.instructor_name || 'Admin'}</span>
              </div>
              <div className={styles.detailRow}>
                <strong>Enrollments:</strong> <span>{viewingCourse.students_enrolled || 0}</span>
              </div>
              <div className={styles.detailRow}>
                <strong>Rating:</strong> <span>{viewingCourse.avg_rating || 0}/5</span>
              </div>
              <div className={styles.detailRow}>
                <strong>Status:</strong> <span className={styles[`status${viewingCourse.status?.charAt(0).toUpperCase() + viewingCourse.status?.slice(1)}`]}>{viewingCourse.status}</span>
              </div>
              {viewingCourse.description && (
                <div className={styles.detailRow} style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                  <strong style={{ marginBottom: '8px' }}>Description:</strong>
                  <p style={{ margin: 0, color: '#666', lineHeight: '1.6' }}>{viewingCourse.description}</p>
                </div>
              )}
            </div>
          </div>

          <div className={styles.modalFooter}>
            <button
              className={`${styles.btn} ${styles.btnSecondary}`}
              onClick={() => setShowViewCourseModal(false)}
            >
              Close
            </button>
            <button
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={() => {
                setShowViewCourseModal(false);
                handleEditCourse(viewingCourse);
              }}
            >
              Edit Course
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderCreateContentModal = () => {
    if (!showCreateContentModal) return null;

    return (
      <div className={styles.modalOverlay} onClick={() => setShowCreateContentModal(false)}>
        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <h3>Create New Content</h3>
            <button className={styles.closeButton} onClick={() => setShowCreateContentModal(false)}>
              <span className="material-icons">close</span>
            </button>
          </div>
          
          <div className={styles.modalBody}>
            <div className={styles.formGroup}>
              <label>Title *</label>
              <input 
                type="text" 
                value={newContent.title}
                onChange={(e) => setNewContent(prev => ({
                  ...prev,
                  title: e.target.value
                }))}
                className={styles.formInput}
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label>Subject *</label>
              <select 
                value={newContent.subject_id}
                onChange={(e) => setNewContent(prev => ({
                  ...prev,
                  subject_id: e.target.value
                }))}
                className={styles.formSelect}
                required
              >
                <option value="">Select a subject</option>
                {subjects.map(subject => (
                  <option key={subject.id} value={subject.id}>{subject.name}</option>
                ))}
              </select>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Content Type</label>
                <select 
                  value={newContent.content_type}
                  onChange={(e) => setNewContent(prev => ({
                    ...prev,
                    content_type: e.target.value
                  }))}
                  className={styles.formSelect}
                >
                  <option value="lesson">Lesson</option>
                  <option value="vocabulary">Vocabulary</option>
                  <option value="reading">Reading</option>
                  <option value="grammar">Grammar</option>
                  <option value="exercise">Exercise</option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label>Difficulty Level</label>
                <select 
                  value={newContent.difficulty_level}
                  onChange={(e) => setNewContent(prev => ({
                    ...prev,
                    difficulty_level: e.target.value
                  }))}
                  className={styles.formSelect}
                >
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label>Content *</label>
              <textarea 
                value={newContent.content}
                onChange={(e) => setNewContent(prev => ({
                  ...prev,
                  content: e.target.value
                }))}
                className={styles.formTextarea}
                rows="6"
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label>
                <input 
                  type="checkbox" 
                  checked={newContent.is_published}
                  onChange={(e) => setNewContent(prev => ({
                    ...prev,
                    is_published: e.target.checked
                  }))}
                />
                Publish immediately
              </label>
            </div>
          </div>

          <div className={styles.modalFooter}>
            <button 
              type="button"
              className={`${styles.btn} ${styles.btnSecondary}`}
              onClick={() => setShowCreateContentModal(false)}
            >
              Cancel
            </button>
            <button 
              type="button"
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={handleSaveContent}
            >
              Create Content
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderAddCourse = () => (
    <div className={styles.addCourseContent}>
      <div className={styles.contentHeader}>
        <h2>{editingCourse ? 'Edit Course' : 'Create New Course'}</h2>
        <p>{editingCourse ? 'Update course information. Modify the fields you want to change.' : 'Add a new course to the library. Fill in all required fields.'}</p>
      </div>

      <form onSubmit={editingCourse ? handleUpdateCourse : handleCreateCourse} className={styles.courseForm}>
        <div className={styles.formSection}>
          <h3>Basic Information</h3>
          
          <div className={styles.formGroup}>
            <label>Course Title *</label>
            <input
              type="text"
              value={newCourse.title}
              onChange={(e) => setNewCourse({...newCourse, title: e.target.value})}
              placeholder="e.g., Complete JavaScript Masterclass"
              required
              className={styles.formInput}
            />
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>Category *</label>
              <select
                value={newCourse.category}
                onChange={(e) => setNewCourse({...newCourse, category: e.target.value})}
                required
                className={styles.formSelect}
              >
                <option value="">Select Category</option>
                <option value="Programming">Programming</option>
                <option value="Design">Design</option>
                <option value="Business">Business</option>
                <option value="Marketing">Marketing</option>
                <option value="Languages">Languages</option>
                <option value="Science">Science</option>
                <option value="Mathematics">Mathematics</option>
                <option value="Arts">Arts</option>
              </select>
            </div>

            <div className={styles.formGroup}>
              <label>Level *</label>
              <select
                value={newCourse.level}
                onChange={(e) => setNewCourse({...newCourse, level: e.target.value})}
                required
                className={styles.formSelect}
              >
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label>Description</label>
            <textarea
              value={newCourse.description}
              onChange={(e) => setNewCourse({...newCourse, description: e.target.value})}
              placeholder="Describe what students will learn in this course..."
              rows="4"
              className={styles.formTextarea}
            />
          </div>
        </div>

        <div className={styles.formSection}>
          <h3>Pricing</h3>
          
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>Price ($) *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={newCourse.price}
                onChange={(e) => setNewCourse({...newCourse, price: e.target.value})}
                placeholder="99.99"
                required
                className={styles.formInput}
              />
            </div>

            <div className={styles.formGroup}>
              <label>Original Price ($)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={newCourse.original_price}
                onChange={(e) => setNewCourse({...newCourse, original_price: e.target.value})}
                placeholder="149.99 (for discounts)"
                className={styles.formInput}
              />
              <small>Leave empty if no discount</small>
            </div>
          </div>
        </div>

        <div className={styles.formSection}>
          <h3>Course Details</h3>
          
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>Duration (minutes)</label>
              <input
                type="number"
                min="0"
                value={newCourse.duration_minutes}
                onChange={(e) => setNewCourse({...newCourse, duration_minutes: parseInt(e.target.value) || 0})}
                placeholder="750 (for 12h 30m)"
                className={styles.formInput}
              />
              <small>{newCourse.duration_minutes > 0 ? `${Math.floor(newCourse.duration_minutes/60)}h ${newCourse.duration_minutes%60}m` : 'Enter minutes'}</small>
            </div>

            <div className={styles.formGroup}>
              <label>Status</label>
              <select
                value={newCourse.status}
                onChange={(e) => setNewCourse({...newCourse, status: e.target.value})}
                className={styles.formSelect}
              >
                <option value="active">Active (Published)</option>
                <option value="draft">Draft</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label>Teacher</label>
            <select
              value={newCourse.teacher_id}
              onChange={(e) => setNewCourse({...newCourse, teacher_id: e.target.value})}
              className={styles.formSelect}
            >
              <option value="">No Teacher (Admin Course)</option>
              {teachers.map(teacher => (
                <option key={teacher.id} value={teacher.id}>
                  {teacher.name}
                </option>
              ))}
            </select>
            <small>Leave empty to create an admin course</small>
          </div>
        </div>

        <div className={styles.formSection}>
          <h3>Images</h3>
          
          {/* Thumbnail Image */}
          <div className={styles.formGroup}>
            <label>Thumbnail Image</label>
            <div className={styles.imageUploadContainer}>
              {thumbnailPreview ? (
                <div className={styles.imagePreviewContainer}>
                  <img src={thumbnailPreview} alt="Thumbnail preview" className={styles.imagePreview} />
                  <button
                    type="button"
                    className={styles.removeImageBtn}
                    onClick={handleRemoveThumbnail}
                    title="Remove image"
                  >
                    <span className="material-icons">close</span>
                  </button>
                </div>
              ) : (
                <div className={styles.uploadArea}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleThumbnailUpload}
                    className={styles.fileInput}
                    id="thumbnail-upload"
                    disabled={uploadingThumbnail}
                  />
                  <label htmlFor="thumbnail-upload" className={styles.uploadLabel}>
                    {uploadingThumbnail ? (
                      <>
                        <span className="material-icons">hourglass_empty</span>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <span className="material-icons">cloud_upload</span>
                        Upload Thumbnail
                      </>
                    )}
                  </label>
                </div>
              )}
              <div className={styles.imageUrlInput}>
                <span className={styles.orDivider}>OR</span>
                <input
                  type="url"
                  value={newCourse.thumbnail_url}
                  onChange={(e) => setNewCourse({...newCourse, thumbnail_url: e.target.value})}
                  placeholder="https://example.com/thumbnail.jpg"
                  className={styles.formInput}
                  disabled={!!thumbnailPreview}
                />
              </div>
            </div>
            <small>Image for course cards (400x225 recommended). Upload file or enter URL.</small>
          </div>

          {/* Cover Image */}
          <div className={styles.formGroup}>
            <label>Cover Image</label>
            <div className={styles.imageUploadContainer}>
              {coverPreview ? (
                <div className={styles.imagePreviewContainer}>
                  <img src={coverPreview} alt="Cover preview" className={styles.imagePreview} />
                  <button
                    type="button"
                    className={styles.removeImageBtn}
                    onClick={handleRemoveCover}
                    title="Remove image"
                  >
                    <span className="material-icons">close</span>
                  </button>
                </div>
              ) : (
                <div className={styles.uploadArea}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleCoverUpload}
                    className={styles.fileInput}
                    id="cover-upload"
                    disabled={uploadingCover}
                  />
                  <label htmlFor="cover-upload" className={styles.uploadLabel}>
                    {uploadingCover ? (
                      <>
                        <span className="material-icons">hourglass_empty</span>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <span className="material-icons">cloud_upload</span>
                        Upload Cover Image
                      </>
                    )}
                  </label>
                </div>
              )}
              <div className={styles.imageUrlInput}>
                <span className={styles.orDivider}>OR</span>
                <input
                  type="url"
                  value={newCourse.cover_image_url}
                  onChange={(e) => setNewCourse({...newCourse, cover_image_url: e.target.value})}
                  placeholder="https://example.com/cover.jpg"
                  className={styles.formInput}
                  disabled={!!coverPreview}
                />
              </div>
            </div>
            <small>Image for course detail page (1200x675 recommended). Upload file or enter URL.</small>
          </div>
        </div>

        <div className={styles.formSection}>
          <h3>Course Videos</h3>
          
          <div className={styles.formGroup}>
            <label>Upload Course Videos</label>
            <div className={styles.videoUploadContainer}>
              <div className={styles.uploadArea}>
                <input
                  type="file"
                  accept="video/*"
                  onChange={handleVideoUpload}
                  className={styles.fileInput}
                  id="video-upload"
                  disabled={uploadingVideo}
                />
                <label htmlFor="video-upload" className={styles.uploadLabel}>
                  {uploadingVideo ? (
                    <>
                      <span className="material-icons">hourglass_empty</span>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <span className="material-icons">video_library</span>
                      Upload Video
                    </>
                  )}
                </label>
              </div>
              <small>Upload course video files (MP4, AVI, MOV, WebM). Max size: 500MB per video.</small>
            </div>

            {courseVideos.length > 0 && (
              <div className={styles.videosList}>
                <h4>Uploaded Videos ({courseVideos.length})</h4>
                {courseVideos.map((video) => (
                  <div key={video.id} className={styles.videoItem}>
                    <div className={styles.videoInfo}>
                      <span className="material-icons">play_circle</span>
                      <input
                        type="text"
                        value={video.title}
                        onChange={(e) => handleVideoTitleChange(video.id, e.target.value)}
                        className={styles.videoTitleInput}
                        placeholder="Video title"
                      />
                      <span className={styles.videoSize}>
                        {(video.size / (1024 * 1024)).toFixed(2)} MB
                      </span>
                      {video.status === 'uploading' && (
                        <span className={styles.uploadStatus}>Uploading...</span>
                      )}
                      {video.status === 'ready' && (
                        <span className={styles.uploadStatusReady}>✓ Ready</span>
                      )}
                    </div>
                    <button
                      type="button"
                      className={styles.removeVideoBtn}
                      onClick={() => handleRemoveVideo(video.id)}
                      title="Remove video"
                    >
                      <span className="material-icons">delete</span>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className={styles.formActions}>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnSecondary}`}
            onClick={() => {
              setActiveTab('courses');
              setNewCourse({
                title: '',
                category: '',
                price: '',
                original_price: '',
                level: 'beginner',
                description: '',
                teacher_id: '',
                duration_minutes: 0,
                thumbnail_url: '',
                cover_image_url: '',
                status: 'active'
              });
              setThumbnailFile(null);
              setThumbnailPreview(null);
              setCoverFile(null);
              setCoverPreview(null);
              setCourseVideos([]);
              setEditingCourse(null);
              setShowEditCourseModal(false);
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={`${styles.btn} ${styles.btnPrimary}`}
            disabled={isCreatingCourse}
          >
            {isCreatingCourse ? (
              <>
                <span className="material-icons">hourglass_empty</span>
                {editingCourse ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                <span className="material-icons">check</span>
                {editingCourse ? 'Update Course' : 'Create Course'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return renderDashboard();
      case 'teachers':
        return renderTeachers();
      case 'courses':
        return renderCourses();
      case 'add-course':
        return renderAddCourse();
      case 'students':
        return renderStudents();
      case 'support-classes':
        return renderSupportClasses();
      case 'payments':
        return renderPayments();
      case 'reviews':
        return renderReviews();
      case 'analytics':
        return renderAnalytics();
      case 'settings':
        return renderSettings();
      default:
        return renderDashboard();
    }
  };

  return (
    <>
      <div className={styles.courseLibraryWrapper}>
        <div className={styles.courseLibraryContent}>
          {onBack && (
            <button className={styles.backToCourseManagement} onClick={onBack}>
              <span className="material-icons">arrow_back</span>
              {t('backToCourseManagement')}
            </button>
          )}
          
          <div className={styles.tabsContainer}>
            {tabs.map(tab => (
              <button
                key={tab.id}
                className={`${styles.tabButton} ${activeTab === tab.id ? styles.active : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <span className="material-icons">{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          <div className={styles.contentArea}>
            {renderContent()}
          </div>
        </div>
      </div>

      {/* Modals rendered outside main wrapper */}
      {renderCreateContentModal()}
      {renderImageEditModal()}
      {renderViewCourseModal()}
      
    </>
  );
};

export default CourseLibrary; 